import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { MatchPair, MatchRound } from '../../../interfaces/match-up.interface';

interface Pt { x: number; y: number; }

interface Tile {
  pair: MatchPair;
  home: Pt;      // resting spot in the tray
  pos: Pt;       // current centre
  placed: boolean;
  dragging: boolean;
}

interface Cell { x: number; y: number; on: boolean; }

interface Slot {
  pair: MatchPair;
  pos: Pt;       // centre
  filled: boolean;
  pips: Pt[];    // dots + dice pip centres
  cells: Cell[]; // ten-frame cells
  exprText: string;
}

interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * The Star Match play surface — a cosmic drag-and-drop board. Glowing
 * number / letter tiles sit in a tray and are dragged up into dashed
 * target slots; a correct drop clicks into place with a sparkle, a
 * wrong drop glides silently back to the tray (no nagging). When every
 * tile is placed the round is solved.
 *
 * Presentation-only, like the symmetry stage: it owns the drag gesture
 * and the snap/return feedback and reports completion via `solved`.
 * Scoring / turns / round flow live in the shell + `MiniGameService`.
 *
 * Tiles are positioned with an SVG `transform` that CSS-transitions, so
 * snap and return animate for free; the transition is suppressed while
 * a finger/mouse is actively dragging so it tracks 1:1.
 */
@Component({
  selector: 'app-match-up-stage',
  templateUrl: './match-up-stage.component.html',
  styleUrls: ['./match-up-stage.component.scss']
})
export class MatchUpStageComponent implements OnChanges {
  @Input() round: MatchRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;

  @Output() solved = new EventEmitter<void>();
  /** Fires on a correct placement (for a soft click SFX in the shell). */
  @Output() placed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1180;
  readonly VIEW_H = 640;
  readonly TILE = 118;         // tile square side
  readonly SLOT = 150;         // slot square side
  private readonly SNAP_DIST = 120;

  tiles: Tile[] = [];
  slots: Slot[] = [];
  sparkles: Sparkle[] = [];

  private dragIndex = -1;
  private grabOffset: Pt = { x: 0, y: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.build();
  }

  private build(): void {
    this.tiles = [];
    this.slots = [];
    this.sparkles = [];
    this.dragIndex = -1;
    if (!this.round) return;

    const pairs = this.round.pairs;
    const n = pairs.length;
    const slotY = 232;
    const trayY = 486;

    const slotXs = this.rowXs(n);
    // Slots keep the pair order; the tray is shuffled so it's a real
    // matching task, not a straight line-up.
    this.slots = pairs.map((pair, i) => ({
      pair,
      pos: { x: slotXs[i], y: slotY },
      filled: false,
      pips: pair.slotKind === 'dots' ? this.dotLayout(pair.count || 0)
          : pair.slotKind === 'dice' ? this.dicePips(pair.count || 0)
          : [],
      cells: pair.slotKind === 'tenframe' ? this.tenFrameCells(pair.count || 0) : [],
      exprText: pair.slotKind === 'expr' ? (pair.exprText || '') : ''
    }));

    const trayOrder = this.shuffleIndices(n);
    const trayXs = this.rowXs(n);
    this.tiles = trayOrder.map((pairIdx, slotSpot) => {
      const home = { x: trayXs[slotSpot], y: trayY };
      return {
        pair: pairs[pairIdx],
        home,
        pos: { ...home },
        placed: false,
        dragging: false
      };
    });
  }

  /** Evenly-spaced x centres for `n` items across the board. */
  private rowXs(n: number): number[] {
    const margin = 140;
    const usable = this.VIEW_W - margin * 2;
    if (n <= 1) return [this.VIEW_W / 2];
    const step = usable / (n - 1);
    return Array.from({ length: n }, (_, i) => margin + step * i);
  }

  private shuffleIndices(n: number): number[] {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    // Guard against the identity ordering (tray == slots) on small n.
    if (n > 1 && a.every((v, i) => v === i)) [a[0], a[1]] = [a[1], a[0]];
    return a;
  }

  /** Scattered-grid pip positions (centred on the slot) for count `d`. */
  private dotLayout(d: number): Pt[] {
    const perRow = d <= 3 ? d : d <= 6 ? 3 : Math.ceil(d / 2);
    const rows = Math.ceil(d / perRow);
    const gap = 30;
    const out: Pt[] = [];
    let placed = 0;
    for (let r = 0; r < rows; r++) {
      const inRow = Math.min(perRow, d - placed);
      for (let c = 0; c < inRow; c++) {
        out.push({ x: (c - (inRow - 1) / 2) * gap, y: (r - (rows - 1) / 2) * gap });
        placed++;
      }
    }
    return out;
  }

  /** Standard dice-face pip positions for 1–6 (centred on the slot). */
  private dicePips(n: number): Pt[] {
    const s = 36, L = -s, R = s, M = 0, T = -s, B = s;
    const faces: Record<number, [number, number][]> = {
      1: [[M, M]],
      2: [[L, T], [R, B]],
      3: [[L, T], [M, M], [R, B]],
      4: [[L, T], [R, T], [L, B], [R, B]],
      5: [[L, T], [R, T], [M, M], [L, B], [R, B]],
      6: [[L, T], [R, T], [L, M], [R, M], [L, B], [R, B]]
    };
    return (faces[n] || []).map(([x, y]) => ({ x, y }));
  }

  /** A 2×5 ten-frame: first `n` cells lit, rest empty (centred). */
  private tenFrameCells(n: number): Cell[] {
    const cols = 5, rows = 2, step = 27;
    const out: Cell[] = [];
    for (let i = 0; i < 10; i++) {
      const c = i % cols;
      const r = Math.floor(i / cols);
      out.push({
        x: (c - (cols - 1) / 2) * step,
        y: (r - (rows - 1) / 2) * step,
        on: i < n
      });
    }
    return out;
  }

  // ── Drag (pointer events: mouse + touch) ───────────────────────────

  onTileDown(i: number, evt: PointerEvent): void {
    if (!this.interactive || this.tiles[i].placed) return;
    evt.preventDefault();
    evt.stopPropagation();
    const p = this.toSvg(evt);
    const t = this.tiles[i];
    this.dragIndex = i;
    this.grabOffset = { x: p.x - t.pos.x, y: p.y - t.pos.y };
    t.dragging = true;
    // Bring to top by moving it to the end of the render list.
    if (i !== this.tiles.length - 1) {
      this.tiles.push(this.tiles.splice(i, 1)[0]);
      this.dragIndex = this.tiles.length - 1;
    }
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onPointerMove(evt: PointerEvent): void {
    if (this.dragIndex < 0) return;
    const p = this.toSvg(evt);
    const t = this.tiles[this.dragIndex];
    t.pos = this.clamp({ x: p.x - this.grabOffset.x, y: p.y - this.grabOffset.y });
  }

  onPointerUp(evt: PointerEvent): void {
    if (this.dragIndex < 0) return;
    const t = this.tiles[this.dragIndex];
    t.dragging = false;
    this.dragIndex = -1;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }

    const target = this.slots.find(s => s.pair.id === t.pair.id);
    if (target && !target.filled && this.near(t.pos, target.pos)) {
      t.pos = { ...target.pos };
      t.placed = true;
      target.filled = true;
      this.spawnSparkles(target.pos);
      this.placed.emit();
      if (this.tiles.every(tile => tile.placed)) {
        setTimeout(() => this.solved.emit(), 250);
      }
    } else {
      // Wrong / far — glide home silently.
      t.pos = { ...t.home };
    }
  }

  private near(a: Pt, b: Pt): boolean {
    return Math.hypot(a.x - b.x, a.y - b.y) <= this.SNAP_DIST;
  }

  private spawnSparkles(c: Pt): void {
    this.sparkles = Array.from({ length: 10 }, (_, i) => {
      const a = (i / 10) * Math.PI * 2;
      const dist = 45 + (i % 3) * 20;
      return { x: c.x + Math.cos(a) * dist, y: c.y + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
    // Clear after the burst so a later match re-triggers cleanly.
    setTimeout(() => { this.sparkles = []; }, 900);
  }

  private clamp(p: Pt): Pt {
    const h = this.TILE / 2 + 6;
    return {
      x: Math.max(h, Math.min(this.VIEW_W - h, p.x)),
      y: Math.max(h, Math.min(this.VIEW_H - h, p.y))
    };
  }

  private toSvg(evt: PointerEvent): Pt {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = pt.matrixTransform(ctm.inverse());
    return { x: m.x, y: m.y };
  }
}
